import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, Alert } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";

const TaskDailyModal = ({ show, onClose, onSave, initialData, taskJDs, kpis = [] }) => {
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    taskJDId: "", projectId: "", kpiId: "", achievedValue: 0, priority: "Normal", taskDetail: "",
    workDate: "", startTime: "", finishTime: "", percentProcess: 0,
    status: "Pending", note: "", hours: 0, userId: ""
  });
  
  const [errors, setErrors] = useState({});
  const [displayInfo, setDisplayInfo] = useState({ code: "", mainJD: "", mainTaskList: "", departmentName: "" });
  const [displayKPI, setDisplayKPI] = useState({ unit: "", targetValue: 0, currentAchieved: 0, description: "" });

  const getUserId = () => {
    const savedId = localStorage.getItem("userId");
    if (savedId) return savedId;
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(window.atob(token.split('.')[1]));
        return payload.nameid || payload.sub || null;
      } catch (e) { return null; }
    }
    return null;
  };

  const currentUserId = getUserId();
  const getNowStr = () => new Date().toISOString().split('T')[0];

  const filteredTaskJDs = (taskJDs || []).filter(t => String(t.userId) === String(currentUserId));
  
  const filteredKPIs = (kpis || []).filter(k => {
    const kId = String(k.id || k.Id);
    const isOwner = String(k.userId) === String(currentUserId);
    const selectedDate = new Date(formData.workDate);
    selectedDate.setHours(0, 0, 0, 0);
    const startDate = k.startDate || k.StartDate ? new Date(k.startDate || k.StartDate) : null;
    const endDate = k.endDate || k.EndDate ? new Date(k.endDate || k.EndDate) : null;
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);
    const isActive = startDate && endDate ? (selectedDate >= startDate && selectedDate <= endDate) : true;
    const isSelected = initialData && kId === String(initialData.kpiId || initialData.KPIId);
    return (isOwner && isActive) || isSelected;
  });

  useEffect(() => {
    if (show) {
      const fetchProjects = async () => {
        try {
          const res = await axiosClient.get(API_ENDPOINTS.projects);
          setProjects(res.data || []);
        } catch (e) { console.error("❌ Error loading Projects:", e); }
      };
      fetchProjects();
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      if (initialData) {
        const kId = initialData.kpiId || initialData.KPIId;
        const mappedData = {
          ...initialData,
          kpiId: kId ? String(kId) : "", 
          achievedValue: initialData.achievedValue || initialData.AchievedValue || 0,
          projectId: initialData.projectId || initialData.ProjectId || "",
          workDate: initialData.workDate ? initialData.workDate.split("T")[0] : getNowStr(),
          startTime: formatTimeForInput(initialData.startTime),
          finishTime: formatTimeForInput(initialData.finishTime),
          hours: parseFloat(initialData.hours) || 0,
          userId: currentUserId
        };
        setFormData(mappedData);
        autoFillInfo(mappedData.taskJDId);
      } else {
        setFormData({
          taskJDId: "", projectId: "", kpiId: "", achievedValue: 0, priority: "Normal", taskDetail: "", 
          workDate: getNowStr(), startTime: "", finishTime: "", percentProcess: 0, 
          status: "Pending", note: "", hours: 0, userId: currentUserId
        });
        setDisplayInfo({ code: "", mainJD: "", mainTaskList: "", departmentName: "" });
        setDisplayKPI({ unit: "", targetValue: 0, currentAchieved: 0, description: "" });
      }
      setErrors({});
    }
  }, [show, initialData]);

  useEffect(() => {
    if (show && formData.kpiId) {
      autoFillKPI(formData.kpiId);
    }
  }, [show, kpis, formData.kpiId]);

  const autoFillInfo = (id) => {
    if (!id) {
      setDisplayInfo({ code: "", mainJD: "", mainTaskList: "", departmentName: "" });
      return;
    }
    const task = (taskJDs || []).find(t => String(t.id || t.Id) === String(id));
    if (task) {
      setDisplayInfo({
        code: task.code || task.Code || "",
        mainJD: task.mainWork || task.mainJD || task.MainWork || "",
        mainTaskList: task.mainTaskList || task.MainTaskList || "",
        departmentName: task.departmentName || (task.department ? task.department.name : "N/A")
      });
    }
  };

  const autoFillKPI = (id) => {
    if (!id) {
      setDisplayKPI({ unit: "", targetValue: 0, currentAchieved: 0, description: "" });
      return;
    }
    const kpi = (kpis || []).find(k => String(k.id || k.Id) === String(id));
    if (kpi) {
      setDisplayKPI({
        unit: kpi.unit || kpi.Unit || "N/A",
        targetValue: kpi.targetValue || kpi.TargetValue || 0,
        currentAchieved: kpi.achievedValue || kpi.AchievedValue || 0, // Trường tổng tích lũy hiện tại của KPI
        description: kpi.description || kpi.Description || ""
      });
    }
  };

  const checkIsPast = (dateInput) => {
    if (!dateInput) return false;
    const inputDate = new Date(dateInput);
    inputDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return inputDate < today;
  };

  const isPastDate = checkIsPast(formData.workDate);
  const isExpiredRecord = initialData && initialData.workDate 
    ? checkIsPast(initialData.workDate.split("T")[0])
    : false;

  const formatTimeForInput = (timeString) => {
    if (!timeString) return "";
    try {
      if (typeof timeString === 'string' && timeString.includes(" ")) {
        const parts = timeString.split(" ");
        const timePart = parts.find(p => p.includes(":"));
        if (timePart) return timePart.substring(0, 5);
      }
      const cleanTime = String(timeString).split(".")[0]; 
      const segments = cleanTime.split(":");
      if (segments.length >= 2) {
        return `${segments[0].padStart(2, '0')}:${segments[1].padStart(2, '0')}`;
      }
    } catch (e) { return ""; }
    return "";
  };

  const calculateHours = (start, finish) => {
    if (!start || !finish) return 0;
    try {
      const [startH, startM] = start.split(':').map(Number);
      const [finishH, finishM] = finish.split(':').map(Number);
      const startDate = new Date(0, 0, 0, startH, startM);
      const finishDate = new Date(0, 0, 0, finishH, finishM);
      let diff = (finishDate.getTime() - startDate.getTime()) / 1000 / 60 / 60;
      return diff > 0 ? parseFloat(diff.toFixed(2)) : 0;
    } catch (e) { return 0; }
  };

  const handleChange = (e) => {
    if (isExpiredRecord) return;
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === "startTime" || name === "finishTime") {
        newData.hours = calculateHours(newData.startTime, newData.finishTime);
      }
      return newData;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    if (name === "taskJDId") autoFillInfo(value);
  };

  const handleValidateAndSave = () => {
    const newErrors = {};
    if (!formData.taskJDId) newErrors.taskJDId = true;
    if (!formData.taskDetail?.trim()) newErrors.taskDetail = true;
    if (!formData.workDate) newErrors.workDate = true;
    if (!formData.startTime) newErrors.startTime = true;
    if (!formData.finishTime) newErrors.finishTime = true;

    if (formData.kpiId && (formData.achievedValue === "" || formData.achievedValue === null)) {
        newErrors.achievedValue = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      ...formData,
      id: initialData?.id || initialData?.Id || 0,
      taskJDId: Number(formData.taskJDId),
      kpiId: formData.kpiId ? Number(formData.kpiId) : null,
      achievedValue: formData.kpiId ? parseFloat(formData.achievedValue) : 0,
      projectId: formData.projectId ? Number(formData.projectId) : null,
      hours: parseFloat(formData.hours) || 0,
      userId: Number(currentUserId)
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton className={`${isExpiredRecord ? "bg-secondary" : "bg-primary"} text-white py-1 px-3`}>
        <Modal.Title style={{fontSize: '0.95rem'}} className="fw-bold">
          {isExpiredRecord ? "🔒 VIEW REPORT" : (initialData?.id || initialData?.Id ? "✏️ UPDATE REPORT" : "➕ SAVE REPORT")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-light p-2" style={{ fontSize: '0.8rem' }}>
        {(isPastDate || isExpiredRecord) && (
          <Alert variant="danger" className="py-1 px-2 small mb-2 border-start border-4 border-danger">
            ⚠️ OVERDUE: Updating past records might be restricted.
          </Alert>
        )}

        <Form>
          {/* Section 1: REFERENCE INFORMATION */}
          <Card className="shadow-sm border-0 mb-2">
            <Card.Header className="bg-white fw-bold text-primary py-1 px-2 small d-flex justify-content-between">
              <span>1. REFERENCE INFORMATION</span>
              <span className="badge bg-light text-dark border">UID: {currentUserId}</span>
            </Card.Header>
            <Card.Body className="py-1 px-2">
              <Row className="gx-2">
                <Col md={12} className="mb-1">
                  <Form.Label className="fw-bold mb-0">Select Main Task <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    name="taskJDId" value={String(formData.taskJDId || "")} onChange={handleChange}
                    isInvalid={!!errors.taskJDId} size="sm" disabled={isExpiredRecord}
                  >
                    <option value="">-- Choose Your Assigned Task --</option>
                    {filteredTaskJDs.map(t => (
                      <option key={String(t.id || t.Id)} value={String(t.id || t.Id)}>{(t.code || t.Code)} - {(t.mainWork || t.mainJD || t.MainWork)}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="text-muted mb-0 small">Code</Form.Label>
                  <Form.Control type="text" value={displayInfo.code || ""} readOnly size="sm" className="bg-light py-0" style={{height: '28px'}} />
                </Col>
                <Col md={9}>
                  <Form.Label className="text-muted mb-0 small">Department</Form.Label>
                  <Form.Control type="text" value={displayInfo.departmentName || ""} readOnly size="sm" className="bg-light py-0" style={{height: '28px'}} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Section 2: KPI REFERENCE */}
          <Card className="shadow-sm border-0 mb-2">
            <Card.Header className="bg-white fw-bold text-success py-1 px-2 small">2. KPI REFERENCE</Card.Header>
            <Card.Body className="py-1 px-2">
              <Row className="gx-2">
                <Col md={8} className="mb-1">
                  <Form.Label className="fw-bold mb-0 small">Select Related KPI</Form.Label>
                  <Form.Select 
                    name="kpiId" 
                    value={String(formData.kpiId || "")} 
                    onChange={handleChange}
                    size="sm" disabled={isExpiredRecord}
                  >
                    <option value="">-- Choose KPI --</option>
                    {filteredKPIs.map(k => (
                      <option key={String(k.id || k.Id)} value={String(k.id || k.Id)}>
                        {(k.kpiCode || k.KPICode)} - {(k.description || k.Description)}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4} className="mb-1">
                  <Form.Label className="fw-bold mb-0 small text-success">
                    Daily Achieved {formData.kpiId && <span className="text-danger">*</span>}
                  </Form.Label>
                  <Form.Control 
                    type="number"
                    name="achievedValue"
                    value={formData.achievedValue}
                    onChange={handleChange}
                    isInvalid={!!errors.achievedValue}
                    size="sm"
                    placeholder="Enter today's result..."
                    disabled={isExpiredRecord || !formData.kpiId}
                  />
                  <Form.Control.Feedback type="invalid">Required when KPI selected</Form.Control.Feedback>
                </Col>

                <Col md={2}>
                  <Form.Label className="text-muted mb-0 small">Unit</Form.Label>
                  <Form.Control value={displayKPI.unit || ""} readOnly size="sm" className="bg-light py-0" style={{height: '28px'}} />
                </Col>
                <Col md={2}>
                  <Form.Label className="text-muted mb-0 small">Target</Form.Label>
                  <Form.Control value={displayKPI.targetValue || 0} readOnly size="sm" className="bg-light py-0 fw-bold" style={{height: '28px'}} />
                </Col>
                {/* 🔥 TRƯỜNG MỚI: TỔNG TÍCH LŨY HIỆN TẠI (CHE MỜ) */}
                <Col md={3}>
                  <Form.Label className="text-muted mb-0 small">Current Total Achieved</Form.Label>
                  <Form.Control value={displayKPI.currentAchieved || 0} readOnly size="sm" className="bg-light py-0 text-primary fw-bold" style={{height: '28px', opacity: 0.7}} />
                </Col>
                <Col md={5}>
                  <Form.Label className="text-muted mb-0 small">KPI Description</Form.Label>
                  <Form.Control value={displayKPI.description || ""} readOnly size="sm" className="bg-light py-0" style={{height: '28px', fontSize: '0.75rem'}} />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Section 3: DAILY WORK PROGRESS */}
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white fw-bold text-danger py-1 px-2 small">3. DAILY WORK PROGRESS</Card.Header>
            <Card.Body className="py-1 px-2">
              <Row className="gx-2">
                <Col md={6} className="mb-1">
                  <Form.Label className="fw-bold mb-0 text-primary small">Project</Form.Label>
                  <Form.Select name="projectId" value={String(formData.projectId || "")} onChange={handleChange} size="sm" disabled={isExpiredRecord}>
                    <option value="">-- No Project --</option>
                    {projects.map(p => (<option key={String(p.id || p.Id)} value={String(p.id || p.Id)}>{(p.code || p.Code)} - {(p.name || p.Name)}</option>))}
                  </Form.Select>
                </Col>
                <Col md={6} className="mb-1">
                  <Form.Label className="fw-bold mb-0 text-primary small">Priority</Form.Label>
                  <Form.Select name="priority" value={formData.priority || "Normal"} onChange={handleChange} size="sm" disabled={isExpiredRecord}>
                    <option value="Low">Low</option><option value="Normal">Normal</option><option value="High">High</option><option value="Urgent">Urgent</option>
                  </Form.Select>
                </Col>
                <Col md={12} className="mb-1">
                  <Form.Label className="fw-bold mb-0 small">Task Detail <span className="text-danger">*</span></Form.Label>
                  <Form.Control as="textarea" rows={2} name="taskDetail" value={formData.taskDetail || ""} onChange={handleChange} isInvalid={!!errors.taskDetail} disabled={isExpiredRecord} />
                </Col>

                <Col md={3}><Form.Label className="fw-bold mb-0 small">Date</Form.Label><Form.Control type="date" name="workDate" size="sm" value={formData.workDate} onChange={handleChange} disabled={isExpiredRecord}/></Col>
                
                <Col md={3}>
                    <Form.Label className="fw-bold mb-0 small">Start</Form.Label>
                    <Form.Control type="time" name="startTime" size="sm" value={formData.startTime} onChange={handleChange} isInvalid={!!errors.startTime} disabled={isExpiredRecord} />
                </Col>

                <Col md={3}>
                    <Form.Label className="fw-bold mb-0 small">Finish</Form.Label>
                    <Form.Control type="time" name="finishTime" size="sm" value={formData.finishTime} onChange={handleChange} isInvalid={!!errors.finishTime} disabled={isExpiredRecord} />
                </Col>

                <Col md={3}><Form.Label className="fw-bold mb-0 text-primary small">Hours</Form.Label><Form.Control value={formData.hours} readOnly size="sm" className="fw-bold bg-light py-0" style={{height: '31px'}}/></Col>

                <Col md={6} className="mt-1">
                  <Form.Label className="fw-bold mb-0 small">Progress: <span className="text-danger">{formData.percentProcess || 0}%</span></Form.Label>
                  <Form.Range name="percentProcess" value={formData.percentProcess || 0} onChange={handleChange} disabled={isExpiredRecord} />
                </Col>
                <Col md={6} className="mt-1">
                  <Form.Label className="fw-bold mb-0 small">Status</Form.Label>
                  <Form.Select name="status" size="sm" value={formData.status} onChange={handleChange} disabled={isExpiredRecord}>
                    <option value="Scheduling">🗓️ Scheduling</option>
                    <option value="Pending">🕒 Pending</option>
                    <option value="Processing">⚙️ Processing</option>
                    <option value="Done">✅ Done</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Modal.Body>

      <Modal.Footer className="py-1 px-3">
        <Button variant="light" size="sm" onClick={onClose}>CANCEL</Button>
        <Button variant="success" size="sm" className="px-4 fw-bold shadow-sm" onClick={handleValidateAndSave} disabled={isExpiredRecord}>
          {initialData?.id || initialData?.Id ? "UPDATE REPORT" : "SAVE REPORT"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}; 

export default TaskDailyModal;